import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { throwError, timer } from 'rxjs';
import { map, retry } from 'rxjs/operators';

/**
 * Backoff schedule (ms) used when the server returns a transient failure
 * loading a file. After an asset upload completes, the backend needs a few
 * seconds to render thumbnails / pyramids — until then the request fails
 * with 500. Five attempts ≈ 62s total.
 */
const RETRY_DELAYS_MS = [2000, 4000, 8000, 16000, 32000];

/**
 * HTTP status codes that mean "asset not ready yet, retry":
 *   - 0   → network / CORS error (a 5xx cross-origin response without
 *           Access-Control-Allow-Origin surfaces in the browser as
 *           status 0).
 *   - 404 → file/thumbnail not generated yet.
 *   - 500/502/503/504 → upstream still processing the upload.
 * Any other status (401/403/etc.) is propagated immediately.
 */
const RETRYABLE_STATUSES = new Set([0, 404, 500, 502, 503, 504]);

@Pipe({
    name: 'preprocess',
    standalone: false
})
export class PreprocessFilePipe implements PipeTransform {
  constructor(private http: HttpClient, private domSanitizer: DomSanitizer) {}

  transform(url: string) {
    if (!url) return;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      // Freshly-uploaded assets (sample thumbnails, reference-strip frames,
      // resource files…) often respond with 500/404 for a brief window while
      // the backend finishes rendering them. Retry transparently so the
      // consumer (`<img [src]="url | preprocess | async">`) eventually
      // receives the blob without needing a page reload.
      retry({
        count: RETRY_DELAYS_MS.length,
        delay: (error: HttpErrorResponse, retryCount: number) => {
          if (!RETRYABLE_STATUSES.has(error?.status)) {
            return throwError(() => error);
          }
          const delay = RETRY_DELAYS_MS[retryCount - 1] ?? RETRY_DELAYS_MS.at(-1);
          return timer(delay);
        },
      }),
      map((e) => this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(e)))
    );
  }
}

@NgModule({ declarations: [PreprocessFilePipe],
    exports: [PreprocessFilePipe], imports: [CommonModule], providers: [provideHttpClient(withInterceptorsFromDi())] })
export class PreprocessFilePipeModule {}
